/**
 * TODO EKP-3606: formalize ~/pages module and Page components
 **/
import { lazy } from 'react';
import type { IntlShape } from 'react-intl';

import { ActivitiesTab as TicketDetailsActivitiesTab } from '~/features/workflows-clm-evolution/tickets/tickets-details/activities/ActivitiesTab';
import { DocumentsTab as TicketDetailsDocumentsTab } from '~/features/workflows-clm-evolution/tickets/tickets-details/documents/DocumentsTab';
import { TasksTab as TicketDetailsTasksTab } from '~/features/workflows-clm-evolution/tickets/tickets-details/tasks/TasksTab';
import { FlagType } from '~/flags/flags';
import NoMatch from '~/pages/404';
import ActivationPage from '~/pages/activation';
import ClientsPage from '~/pages/admin';
import AdminCreditsPage from '~/pages/admin/[clientId]/credits';
import AdminRolesPage from '~/pages/admin/[clientId]/roles';
import AdminUsersPage from '~/pages/admin/[clientId]/users';
import AdminNavsPage from '~/pages/admin/console/[clientId]';
import ApiManagementPage from '~/pages/admin/console/[clientId]/api-management';
import AuditLogsPage from '~/pages/admin/console/[clientId]/audit-logs';
import CopyWorkflowPage from '~/pages/admin/console/[clientId]/copy-workflow';
import CRMPage from '~/pages/admin/console/[clientId]/crm';
import DataFieldsPage from '~/pages/admin/console/[clientId]/data-fields';
import DepartmentsPage from '~/pages/admin/console/[clientId]/departments';
import DepartmentPage from '~/pages/admin/console/[clientId]/departments/[departmentId]';
import DocumentManagementPage from '~/pages/admin/console/[clientId]/document-management';
import EmailIntakePage from '~/pages/admin/console/[clientId]/email-intake';
import IntegrationsPage from '~/pages/admin/console/[clientId]/integrations';
import PartyListsPage from '~/pages/admin/console/[clientId]/manage-party-lists';
import MoreSettingsPage from '~/pages/admin/console/[clientId]/more-settings';
import DocumentProcessingPage from '~/pages/admin/console/[clientId]/process-documents';
import ProcurementPage from '~/pages/admin/console/[clientId]/procurement';
import DocumentsRestore from '~/pages/admin/console/[clientId]/restoration-hub';
import SSOConfigPage from '~/pages/admin/console/[clientId]/saml-configuration';
import SAMLConfigPage from '~/pages/admin/console/[clientId]/saml-configuration/form/[configId]';
import UsersPage from '~/pages/admin/console/[clientId]/users';
import WebhooksPage from '~/pages/admin/console/[clientId]/webhooks';
import AgentForgeInvokePage from '~/pages/agentforge-invoke';
import AlertsPage from '~/pages/alerts';
import AnalyzerPage from '~/pages/analyzer';
import AnalyzerResultsPage from '~/pages/analyzer/results';
import AutomationHubPage from '~/pages/automation';
import AutomationHubDetailPage from '~/pages/automation/detail';
import RoutingTablePage from '~/pages/automation/routing-table';
import ChangePasswordPage from '~/pages/change-password';
import ClauseLibrary from '~/pages/clause-library';
import TicketRerouteRedirectPage from '~/pages/contract';
import DashboardsPage from '~/pages/dashboards/[dashboardId]';
import DocumentTreePage from '~/pages/document-group/[groupId]';
import DocumentGroupListPage from '~/pages/document-groups/index';
import DocumentRedirect from '~/pages/document/';
import DocumentsViewPage from '~/pages/document/[documentId]';
import DocumentsPage from '~/pages/documents/[folderId]';
import DownloadEmailAttachmentPage from '~/pages/download-email-attachment';
import DownloadExportReportPage from '~/pages/download-export-report';
import TicketEmailApproveConfirmation from '~/pages/email-action-approve-succeeds';
import TicketEmailRejectConfirmation from '~/pages/email-action-reject-succeeds';
import ExpiredTokenPage from '~/pages/expired-token';
import ExternalDocumentViewerPage from '~/pages/external-document-viewer';
import Fields from '~/pages/fields';
import FieldModel from '~/pages/fields/[modelId]';
import DebugModel from '~/pages/fields/[modelId]/debug';
import PermissionFromSSOPage from '~/pages/folders-permission-check';
import ForgotPasswordPage from '~/pages/forgot-password';
import GuidelinesPage from '~/pages/guidelines';
import TicketEmailActionError from '~/pages/invalid-email-action-token';
import LoginPage from '~/pages/login';
import LoginEmbedPage from '~/pages/login/embed';
import LoginEmbedSuccessPage from '~/pages/login/embed/success';
import ManagedEntityPartiesPage from '~/pages/managed-entity-parties';
import Onboarding from '~/pages/onboarding';
import Playbooks from '~/pages/playbooks';
import RBACPage from '~/pages/rbac';
import SavedDashboards from '~/pages/saved-dashboards';
import SavedSearches from '~/pages/saved-searches';
import SearchV2 from '~/pages/search';
import SettingsPage from '~/pages/settings';
import SuperAdminUsersPage from '~/pages/super-admin/users';
import TicketEmailApproveComment from '~/pages/tickets/[ticketId]/judgment-results/[judgementResultId]/[jwt]/approve';
import TicketEmailRejectComment from '~/pages/tickets/[ticketId]/judgment-results/[judgementResultId]/[jwt]/reject';
import Unauthorized from '~/pages/unauthorized';
import UploadPage from '~/pages/upload';
import WordOnlineErrorPage from '~/pages/word-online-invalid-subscription';
import WordOnlinePage from '~/pages/word-online/[file-id]';
import WizardTicketForm from '~/pages/workflow/[workflowId]/intake-form/link';
import WorkflowBuilderDashboardPage from '~/pages/workflow/builder';
import WorkflowBuilderPage from '~/pages/workflow/builder/[workflowId]';
import WorkflowGroupsPage, {
  DocumentGroupsNewPage,
} from '~/pages/workflow/groups';
import WorkflowGroupDetailPage from '~/pages/workflow/groups/[groupId]';
import WorkflowTicketsPage from '~/pages/workflow/tickets';
import TicketsViewPage from '~/pages/workflow/tickets/[ticketId]';
import WorkflowTicketsNewPage from '~/pages/workflow/tickets/new';
import WorkflowCLMEvolutionTicketsPage from '~/pages/workflows-clm-evolution/tickets';
import WorkflowCLMEvolutionTicketsTicketDetailPage from '~/pages/workflows-clm-evolution/tickets/[ticketId]';
import WorkflowCLMEvolutionTicketsNewPage from '~/pages/workflows-clm-evolution/tickets/new';
import WorkflowCLMEvolutionWorkflowsPage from '~/pages/workflows-clm-evolution/workflows';
import WorkflowCLMEvolutionWorkflowDetailPage from '~/pages/workflows-clm-evolution/workflows/[workflowId]';
import XRayLibrary from '~/pages/x-ray/library';
import XRayLibraryAdmin from '~/pages/x-ray/library/admin';
import { canUseAutomation } from '~/permissions/canUseAutomation';
import { canUseRestorationHub } from '~/permissions/canUseDocumentRestore';
import { canUseManagedEntities } from '~/permissions/canUseManagedEntities';
import { canUsePlaybooks } from '~/permissions/canUsePlaybooks';
import { useSearchAsHomePage as canUseSearchAsHomePage } from '~/permissions/canUseSearchAsHomePage';
import { canUseSearchV2 } from '~/permissions/canUseSearchV2';
import { canUseUpload } from '~/permissions/canUseUpload';
import { canUseUsersPage } from '~/permissions/canUseUsersPage';
import { canUseWorkflow } from '~/permissions/canUseWorkflow';
import { canUseWorkflowAdmin } from '~/permissions/canUseWorkflowAdmin';
import { checkIsAdmin } from '~/permissions/checkIsAdmin';
import { checkIsWorkflowOnly } from '~/permissions/checkIsWorkflowOnly';
import { checkIsRbac } from '~/permissions/rbac/checkIsRbac';
import { RoutePathType } from '~/routing/enums';
import { createAdminResolvers } from '~/routing/resolvers/createAdminResolvers';
import { createDefaultResolvers } from '~/routing/resolvers/createDefaultResolvers';
import { createPassthroughResolvers } from '~/routing/resolvers/createPassthroughResolvers';
import { createSuperAdminResolvers } from '~/routing/resolvers/createSuperAdminResolvers';
import { createWorkflowAdminResolvers } from '~/routing/resolvers/createWorkflowAdminResolvers';
import { createWorkflowResolvers } from '~/routing/resolvers/createWorkflowResolvers';
import { Routes } from '~/routing/types';

import { ClauseView } from '../ClauseLibrary/ClauseView';

const TicketReviewerPage = lazy(
  () => import('~/pages/workflow/tickets/[ticketId]/reviewer/[versionNumber]'),
);

export const privateRoutes = (intl: IntlShape) => {
  const title = intl.formatMessage({
    id: 'Routes.adminConsoleTitle',
    defaultMessage: 'Admin Console',
    description: 'Title for the admin console page route',
  });

  return {
    [RoutePathType.Home]: {
      isPublic: false,
      path: RoutePathType.Home,
      title: intl.formatMessage({
        id: 'Routes.homeTitle',
        defaultMessage: 'Home',
        description: 'Title for the home page route',
      }),
      resolvers: [
        {
          permission: checkIsRbac,
          component: RBACPage,
        },
        {
          permission: checkIsWorkflowOnly,
          redirectPath: RoutePathType.WorkflowTickets,
        },
        {
          permission: canUseSearchAsHomePage,
          redirectPath: RoutePathType.Search,
        },
        {
          redirectPath: RoutePathType.Documents,
        },
      ],
    },
    [RoutePathType.Admin]: {
      isPublic: false,
      path: RoutePathType.Admin,
      title,
      resolvers: createAdminResolvers(ClientsPage),
    },
    [RoutePathType.AdminConsole]: {
      isPublic: false,
      path: RoutePathType.AdminConsole,
      title,
      resolvers: createAdminResolvers(AdminNavsPage),
    },
    [RoutePathType.AdminConsoleClient]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClient,
      title,
      resolvers: createAdminResolvers(AdminNavsPage),
    },
    [RoutePathType.AdminConsoleClientAuditLogs]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientAuditLogs,
      title: intl.formatMessage({
        id: 'Routes.auditLogsTitle',
        defaultMessage: 'Audit Logs',
        description: 'Title for the audit logs page route',
      }),
      permissions: [
        {
          resourceId: 'audit_logs',
          resourceType: 'view',
        },
      ],
      resolvers: createAdminResolvers(AuditLogsPage),
    },
    [RoutePathType.AdminConsoleClientCopyWorkflow]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientCopyWorkflow,
      title: intl.formatMessage({
        id: 'Routes.copyWorkflowTitle',
        defaultMessage: 'Copy Workflow',
        description: 'Title for the copy workflow page route',
      }),
      resolvers: createSuperAdminResolvers(CopyWorkflowPage),
    },
    [RoutePathType.AdminConsoleClientDataFields]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientDataFields,
      title: intl.formatMessage({
        id: 'Routes.dataFieldsTitle',
        defaultMessage: 'Data Fields',
        description: 'Title for the data fields page route',
      }),
      resolvers: createAdminResolvers(DataFieldsPage),
      permissions: [
        {
          resourceId: 'data_fields',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientDepartments]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientDepartments,
      title: intl.formatMessage({
        id: 'Routes.departmentsTitle',
        defaultMessage: 'Departments',
        description: 'Title for the departments page route',
      }),
      resolvers: createAdminResolvers(DepartmentsPage),
      permissions: [
        {
          resourceId: 'departments',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientDepartmentsDepartment]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientDepartmentsDepartment,
      title: intl.formatMessage({
        id: 'Routes.departmentDetailsTitle',
        defaultMessage: 'Department Details',
        description: 'Title for the department details page route',
      }),
      resolvers: createAdminResolvers(DepartmentPage),
      permissions: [
        {
          resourceId: 'departments',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientEmailIntake]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientEmailIntake,
      title: intl.formatMessage({
        id: 'Routes.emailIntakeTitle',
        defaultMessage: 'Email Intake',
        description: 'Title for the email intake page route',
      }),
      resolvers: [
        {
          permission: checkIsAdmin,
          component: EmailIntakePage,
        },
        {
          redirectPath: RoutePathType.AdminConsoleClient,
        },
      ],
      permissions: [
        {
          resourceId: 'email_intake',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientProcurement]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientProcurement,
      title: intl.formatMessage({
        id: 'Routes.procurementTitle',
        defaultMessage: 'Procurement',
        description: 'Title for the procurement page route',
      }),
      resolvers: createAdminResolvers(ProcurementPage),
      permissions: [
        {
          resourceId: 'integrations',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientIntegrations]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientIntegrations,
      title: intl.formatMessage({
        id: 'Routes.integrationsTitle',
        defaultMessage: 'Integrations',
        description: 'Title for the integrations page route',
      }),
      resolvers: createAdminResolvers(IntegrationsPage),
      permissions: [
        {
          resourceId: 'integrations',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientManagePartyLists]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientManagePartyLists,
      title: intl.formatMessage({
        id: 'Routes.managePartyListsTitle',
        defaultMessage: 'Manage Party Lists',
        description: 'Title for the manage party lists page route',
      }),
      resolvers: createAdminResolvers(PartyListsPage),
    },
    [RoutePathType.AdminConsoleClientMoreSettings]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientMoreSettings,
      title: intl.formatMessage({
        id: 'Routes.moreSettingsTitle',
        defaultMessage: 'More Settings',
        description: 'Title for the more settings page route',
      }),
      resolvers: createAdminResolvers(MoreSettingsPage),
      permissions: [
        {
          resourceId: 'root_folder_access_settings',
          resourceType: 'view',
        },
        {
          resourceId: 'documents_metadata_bulk_upload',
          resourceType: 'view',
        },
        {
          resourceId: 'clause_samples',
          resourceType: 'view',
        },
        {
          resourceId: 'deleted_documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientProcessDocuments]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientProcessDocuments,
      title: intl.formatMessage({
        id: 'Routes.processDocumentsTitle',
        defaultMessage: 'Process Documents',
        description: 'Title for the process documents page route',
      }),
      resolvers: createSuperAdminResolvers(DocumentProcessingPage),
    },
    [RoutePathType.AdminConsoleClientRestorationHub]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientRestorationHub,
      title: intl.formatMessage({
        id: 'Routes.restorationHubTitle',
        defaultMessage: 'Restoration Hub',
        description: 'Title for the restoration hub page route',
      }),
      resolvers: [
        {
          permission: canUseRestorationHub,
          component: DocumentsRestore,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'restoration_hub',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientSamlConfiguration]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientSamlConfiguration,
      title: intl.formatMessage({
        id: 'Routes.samlConfigurationTitle',
        defaultMessage: 'SAML Configuration',
        description: 'Title for the SAML configuration page route',
      }),
      resolvers: createAdminResolvers(SSOConfigPage),
      permissions: [
        {
          resourceId: 'sso',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientSamlConfigurationFormConfig]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientSamlConfigurationFormConfig,
      title: intl.formatMessage({
        id: 'Routes.samlConfigurationFormTitle',
        defaultMessage: 'SAML Configuration',
        description: 'Title for the SAML configuration form page route',
      }),
      resolvers: createAdminResolvers(SAMLConfigPage),
      permissions: [
        {
          resourceId: 'sso',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientUsers]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientUsers,
      title: intl.formatMessage({
        id: 'Routes.manageUsersTitle',
        defaultMessage: 'Manage Users',
        description: 'Title for the manage users page route',
      }),
      resolvers: createAdminResolvers(UsersPage),
      permissions: [
        {
          resourceId: 'users',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientDocumentManagement]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientDocumentManagement,
      title: intl.formatMessage({
        id: 'Routes.adminConsoleDocumentManagementTitle',
        defaultMessage: 'Document Management',
        description:
          'Title for the admin console document management page route',
      }),
      resolvers: createAdminResolvers(DocumentManagementPage),
    },
    [RoutePathType.AdminConsoleClientCRM]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientCRM,
      title: intl.formatMessage({
        id: 'Routes.adminConsoleCRMTitle',
        defaultMessage: 'CRM',
        description: 'Title for the admin console crm page route',
      }),
      resolvers: createAdminResolvers(CRMPage),
      permissions: [
        {
          resourceId: 'integrations',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientApiManagement]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientApiManagement,
      title: intl.formatMessage({
        id: 'Routes.adminConsoleApiManagementTitle',
        defaultMessage: 'API Management',
        description: 'Title for the admin console api management page route',
      }),
      resolvers: createAdminResolvers(ApiManagementPage),
      permissions: [
        {
          resourceId: 'integrations',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminConsoleClientWebhooks]: {
      isPublic: false,
      path: RoutePathType.AdminConsoleClientWebhooks,
      title: intl.formatMessage({
        id: 'Routes.adminConsoleWebhooksTitle',
        defaultMessage: 'Webhooks',
        description: 'Title for the admin console webhooks page route',
      }),
      resolvers: createAdminResolvers(WebhooksPage),
      permissions: [
        {
          resourceId: 'integrations',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminClientCredits]: {
      isPublic: false,
      path: RoutePathType.AdminClientCredits,
      title: intl.formatMessage({
        id: 'Routes.creditsTitle',
        defaultMessage: 'Credits',
        description: 'Title for the credits page route',
      }),
      resolvers: createAdminResolvers(AdminCreditsPage),
    },
    [RoutePathType.AdminRoles]: {
      isPublic: false,
      path: RoutePathType.AdminRoles,
      title: intl.formatMessage({
        id: 'Routes.rolesTitle',
        defaultMessage: 'Roles',
        description: 'Title for the roles page route',
      }),
      resolvers: createDefaultResolvers(AdminRolesPage),
      permissions: [
        {
          resourceId: 'roles',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AdminUsers]: {
      isPublic: false,
      path: RoutePathType.AdminUsers,
      title: intl.formatMessage({
        id: 'Routes.usersTitle',
        defaultMessage: 'Users',
        description: 'Title for the users page route',
      }),
      resolvers: [
        {
          permission: canUseUsersPage,
          component: AdminUsersPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'users',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.Alerts]: {
      isPublic: false,
      path: RoutePathType.Alerts,
      title: intl.formatMessage({
        id: 'Routes.alertsTitle',
        defaultMessage: 'Alerts',
        description: 'Title for the alerts page route',
      }),
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          redirectPath: RoutePathType.Home,
        },
        {
          component: AlertsPage,
        },
      ],
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.Analyzer]: {
      isPublic: false,
      path: RoutePathType.Analyzer,
      title: intl.formatMessage({
        id: 'Routes.analyzerTitle',
        defaultMessage: 'Analyzer',
        description: 'Title for the analyzer page route',
      }),
      resolvers: createDefaultResolvers(AnalyzerPage),
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AnalyzerResults]: {
      isPublic: false,
      path: RoutePathType.AnalyzerResults,
      title: intl.formatMessage({
        id: 'Routes.analyzerResultsTitle',
        defaultMessage: 'Analyzer Results',
        description: 'Title for the analyzer results page route',
      }),
      resolvers: createDefaultResolvers(AnalyzerResultsPage),
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.ClauseLibrary]: {
      isPublic: false,
      path: RoutePathType.ClauseLibrary,
      title: intl.formatMessage({
        id: 'Routes.clauseLibraryTitle',
        defaultMessage: 'Clause Library',
        description: 'Title for the clause library page route',
      }),
      flag: FlagType.ClauseLibrary,
      resolvers: [
        {
          /* TODO: Add isClauseLibraryAdmin permission check */
          //permission: canUseClauseLibrary,
          component: ClauseLibrary,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'clause_library',
          resourceType: 'view',
        },
      ],
      nestedRoutes: [
        {
          path: RoutePathType.ClauseLibraryInstance,
          title: intl.formatMessage({
            id: 'Routes.clauseLibraryTitle',
            defaultMessage: 'Clause Library',
            description: 'Title for the clause library page route',
          }),
          component: ClauseView,
        },
      ],
    },
    [RoutePathType.Dashboards]: {
      isPublic: false,
      path: RoutePathType.Dashboards,
      title: intl.formatMessage({
        id: 'Routes.dashboardsTitle',
        defaultMessage: 'Dashboards',
        description: 'Title for the dashboards page route',
      }),
      resolvers: createDefaultResolvers(DashboardsPage),
      permissions: [
        {
          resourceId: 'workflows_dashboard',
          resourceType: 'view',
        },
        {
          resourceId: 'documents_dashboard',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.Dashboard]: {
      isPublic: false,
      path: RoutePathType.Dashboard,
      title: intl.formatMessage({
        id: 'Routes.dashboardsTitle',
        defaultMessage: 'Dashboards',
        description: 'Title for the dashboards page route',
      }),
      resolvers: createDefaultResolvers(DashboardsPage),
      permissions: [
        {
          resourceId: 'workflows_dashboard',
          resourceType: 'view',
        },
        {
          resourceId: 'documents_dashboard',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AutomationHub]: {
      isPublic: false,
      path: RoutePathType.AutomationHub,
      title: intl.formatMessage({
        id: 'Routes.automationTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation page route',
      }),
      resolvers: [
        {
          permission: canUseAutomation,
          component: AutomationHubPage,
        },
        {
          permission: canUseWorkflowAdmin,
          redirectPath: RoutePathType.RoutingTable,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'clause_samples',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AutomationHubFields]: {
      isPublic: false,
      path: RoutePathType.AutomationHubFields,
      title: intl.formatMessage({
        id: 'Routes.automationFieldsTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation fields page route',
      }),
      resolvers: [
        {
          permission: canUseAutomation,
          component: Fields,
        },
        {
          permission: canUseWorkflowAdmin,
          redirectPath: RoutePathType.RoutingTable,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
    [RoutePathType.AutomationHubFieldModel]: {
      isPublic: false,
      path: RoutePathType.AutomationHubFieldModel,
      title: intl.formatMessage({
        id: 'Routes.automationFieldModelTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation field model page route',
      }),
      resolvers: [
        {
          permission: canUseAutomation,
          component: FieldModel,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
    [RoutePathType.AutomationHubFieldModelDebug]: {
      isPublic: false,
      path: RoutePathType.AutomationHubFieldModelDebug,
      title: intl.formatMessage({
        id: 'Routes.automationFieldModelDebugTitle',
        defaultMessage: 'Custom AI Model Debug',
        description: 'Title for the Custom AI model debug page route',
      }),
      flag: FlagType.CustomAiModelDebug,
      resolvers: [
        {
          permission: canUseAutomation,
          component: DebugModel,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
    [RoutePathType.AutomationHubDetail]: {
      isPublic: false,
      path: RoutePathType.AutomationHubDetail,
      title: intl.formatMessage({
        id: 'Routes.automationDetailTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation detail page route',
      }),
      resolvers: [
        {
          permission: canUseAutomation,
          component: AutomationHubDetailPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'clause_samples',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.RoutingTable]: {
      isPublic: false,
      path: RoutePathType.RoutingTable,
      title: intl.formatMessage({
        id: 'Routes.routingTableTitle',
        defaultMessage: 'Routing Table',
        description: 'Title for the routing table page route',
      }),
      flag: FlagType.WorkflowRoutingAgent,
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          redirectPath: RoutePathType.WorkflowTickets,
        },
        {
          component: RoutingTablePage,
        },
      ],
      permissions: [
        {
          resourceId: 'workflow_templates',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.AgentForgeInvoke]: {
      isPublic: false,
      path: RoutePathType.AgentForgeInvoke,
      title: intl.formatMessage({
        id: 'Routes.agentForgeInvokeTitle',
        defaultMessage: 'Agent Forge Invoke',
        description: 'Title for the agent forge invoke page route',
      }),
      flag: FlagType.DevOnlyAgentForgeInvoke,
      resolvers: [
        {
          component: AgentForgeInvokePage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
    [RoutePathType.Onboarding]: {
      isPublic: false,
      path: RoutePathType.Onboarding,
      title: intl.formatMessage({
        id: 'Routes.onboardingTitle',
        defaultMessage: 'Onboarding',
        description: 'Title for the onboarding page route',
      }),
      flag: FlagType.CanduScript,
      resolvers: [
        {
          component: Onboarding,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
    [RoutePathType.Documents]: {
      isPublic: false,
      path: RoutePathType.Documents,
      title: intl.formatMessage({
        id: 'Routes.documentsTitle',
        defaultMessage: 'Documents',
        description: 'Title for the documents page route',
      }),
      resolvers: createDefaultResolvers(DocumentsPage),
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.DocumentsFolder]: {
      isPublic: false,
      path: RoutePathType.DocumentsFolder,
      title: intl.formatMessage({
        id: 'Routes.documentsFolderTitle',
        defaultMessage: 'Documents',
        description: 'Title for the documents folder page route',
      }),
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          redirectPath: RoutePathType.Home,
        },
        {
          component: DocumentsPage,
        },
      ],
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.DocumentGroupsNew]: {
      isPublic: false,
      path: RoutePathType.DocumentGroupsNew,
      title: intl.formatMessage({
        id: 'Routes.groupsTitle',
        defaultMessage: 'Groups',
        description: 'Title for the groups page route',
      }),
      resolvers: createDefaultResolvers(DocumentGroupsNewPage),
      permissions: [
        {
          resourceId: 'entity_groups',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.Document]: {
      isPublic: false,
      path: RoutePathType.Document,
      title: intl.formatMessage({
        id: 'Routes.documentDetailsTitle',
        defaultMessage: 'Document Details',
        description: 'Title for the document details page route',
      }),
      resolvers: createDefaultResolvers(DocumentsViewPage),
      permissions: [],
    },
    [RoutePathType.DocumentRedirect]: {
      isPublic: false,
      path: RoutePathType.DocumentRedirect,
      title: intl.formatMessage({
        id: 'Routes.documentRedirectTitle',
        defaultMessage: 'Document Details',
        description: 'Title for the document redirect page route',
      }),
      resolvers: createDefaultResolvers(DocumentRedirect),
      permissions: [],
    },
    [RoutePathType.DocumentGroups]: {
      isPublic: false,
      path: RoutePathType.DocumentGroups,
      title: intl.formatMessage({
        id: 'Routes.groupsTitle',
        defaultMessage: 'Groups',
        description: 'Title for the groups page route',
      }),
      resolvers: createDefaultResolvers(DocumentGroupListPage),
      permissions: [
        {
          resourceId: 'document_groups',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.DocumentGroupGroup]: {
      isPublic: false,
      path: RoutePathType.DocumentGroupGroup,
      title: intl.formatMessage({
        id: 'Routes.groupDetailsTitle',
        defaultMessage: 'Group details',
        description: 'Title for the group details page route',
      }),
      resolvers: createPassthroughResolvers(DocumentTreePage),
      permissions: [
        {
          resourceId: 'document_groups',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.DocumentsGroupDetail]: {
      isPublic: false,
      path: RoutePathType.DocumentsGroupDetail,
      flag: FlagType.GroupedTicketsAndDocs,
      title: intl.formatMessage({
        id: 'Routes.documentsGroupDetailTitle',
        defaultMessage: 'Group details',
        description: 'Title for the documents group detail page route',
      }),
      resolvers: createDefaultResolvers(WorkflowGroupDetailPage),
      permissions: [
        {
          resourceId: 'entity_groups',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.DownloadEmailAttachmentPage]: {
      isPublic: false,
      path: RoutePathType.DownloadEmailAttachmentPage,
      title: intl.formatMessage({
        id: 'Routes.downloadEmailAttachmentTitle',
        defaultMessage: 'Download Email Attachment',
        description: 'Title for the download email attachment page route',
      }),
      resolvers: createDefaultResolvers(DownloadEmailAttachmentPage),
    },
    [RoutePathType.DownloadExportReportPage]: {
      isPublic: false,
      path: RoutePathType.DownloadExportReportPage,
      title: intl.formatMessage({
        id: 'Routes.downloadExcelExportTitle',
        defaultMessage: 'Download Excel Export',
        description: 'Title for the download Excel export page route',
      }),
      resolvers: createDefaultResolvers(DownloadExportReportPage),
    },
    [RoutePathType.FoldersPermissionCheck]: {
      isPublic: false,
      path: RoutePathType.FoldersPermissionCheck,
      title: intl.formatMessage({
        id: 'Routes.foldersPermissionCheckTitle',
        defaultMessage: 'Folders Permission Check',
        description: 'Title for the folders permission check page route',
      }),
      resolvers: createDefaultResolvers(PermissionFromSSOPage),
    },
    [RoutePathType.ManagedEntities]: {
      isPublic: false,
      path: RoutePathType.ManagedEntities,
      title: intl.formatMessage({
        id: 'Routes.managedEntityPartiesTitle',
        defaultMessage: 'managed entity parties',
        description: 'Title for the managed entity parties page route',
      }),
      resolvers: [
        {
          permission: canUseManagedEntities,
          component: ManagedEntityPartiesPage,
        },
        // TODO: add a default redirect
      ],
      // TODO: add RBAC permissions
    },
    [RoutePathType.MyDeletedDocuments]: {
      isPublic: false,
      path: RoutePathType.MyDeletedDocuments,
      title: intl.formatMessage({
        id: 'Routes.myDeletedItemsTitle',
        defaultMessage: 'My Deleted Items',
        description: 'Title for the my deleted items page route',
      }),
      resolvers: createPassthroughResolvers(DocumentsRestore),
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.Playbooks]: {
      isExact: false,
      isPublic: false,
      path: RoutePathType.Playbooks,
      title: intl.formatMessage({
        id: 'Routes.playbooksTitle',
        defaultMessage: 'Playbooks',
        description: 'Title for the playbooks page route',
      }),
      resolvers: [
        {
          permission: canUsePlaybooks,
          component: Playbooks,
        },
        {
          redirectPath: RoutePathType.ClauseLibrary,
        },
      ],
      permissions: [
        {
          resourceId: 'clause_library',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.Guidelines]: {
      isExact: false,
      isPublic: false,
      path: RoutePathType.Guidelines,
      title: intl.formatMessage({
        id: 'Routes.GuidelinesTitle',
        defaultMessage: 'Guidelines',
        description: 'Title for the guidelines page route',
      }),
      resolvers: [
        {
          permission: canUsePlaybooks,
          component: GuidelinesPage,
        },
        {
          redirectPath: RoutePathType.ClauseLibrary,
        },
      ],
      permissions: [
        {
          resourceId: 'clause_library',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.SavedSearches]: {
      isPublic: false,
      path: RoutePathType.SavedSearches,
      title: intl.formatMessage({
        id: 'Routes.savedSearchesTitle',
        defaultMessage: 'Saved Searches',
        description: 'Title for the saved searches page route',
      }),
      resolvers: [
        {
          permission: canUseSearchV2,
          component: SavedSearches,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.SavedDashboards]: {
      isPublic: false,
      path: RoutePathType.SavedDashboards,
      title: intl.formatMessage({
        id: 'Routes.savedDashboardsTitle',
        defaultMessage: 'Saved Dashboards',
        description: 'Title for the saved dashboards page route',
      }),
      resolvers: [
        {
          component: SavedDashboards,
        },
      ],
      permissions: [
        {
          resourceId: 'workflows_dashboard',
          resourceType: 'view',
        },
        {
          resourceId: 'documents_dashboard',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.SavedSearch]: {
      isPublic: false,
      path: RoutePathType.SavedSearch,
      title: intl.formatMessage({
        id: 'Routes.searchTitle',
        defaultMessage: 'Search',
        description: 'Title for the search page route',
      }),
      flag: FlagType.SearchV3,
      resolvers: [
        {
          permission: canUseSearchV2,
          component: SearchV2,
        },
        {
          redirectPath: RoutePathType.Search,
        },
      ],
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.Search]: {
      isPublic: false,
      path: RoutePathType.Search,
      title: intl.formatMessage({
        id: 'Routes.searchTitle2',
        defaultMessage: 'Search',
        description: 'Title for the search page route',
      }),
      resolvers: [
        {
          permission: canUseSearchV2,
          component: SearchV2,
        },
        {
          redirectPath: RoutePathType.Analyzer,
        },
      ],
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.Settings]: {
      isPublic: false,
      path: RoutePathType.Settings,
      title: intl.formatMessage({
        id: 'Routes.settingsTitle',
        defaultMessage: 'Settings',
        description: 'Title for the settings page route',
      }),
      resolvers: createPassthroughResolvers(SettingsPage),
    },
    [RoutePathType.SuperAdminUsers]: {
      isPublic: false,
      path: RoutePathType.SuperAdminUsers,
      title: intl.formatMessage({
        id: 'Routes.superAdminUsersTitle',
        defaultMessage: 'Users',
        description: 'Title for the super admin users page route',
      }),
      resolvers: createSuperAdminResolvers(SuperAdminUsersPage),
    },
    [RoutePathType.Upload]: {
      isPublic: false,
      path: RoutePathType.Upload,
      title: intl.formatMessage({
        id: 'Routes.uploadTitle',
        defaultMessage: 'Upload',
        description: 'Title for the upload page route',
      }),
      resolvers: [
        {
          permission: canUseUpload,
          component: UploadPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'documents',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowBuilder]: {
      isPublic: false,
      path: RoutePathType.WorkflowBuilder,
      title: intl.formatMessage({
        id: 'Routes.workflowBuilderTitle',
        defaultMessage: 'Workflow Builder',
        description: 'Title for the workflow builder page route',
      }),
      resolvers: createWorkflowAdminResolvers(WorkflowBuilderDashboardPage),
      permissions: [
        {
          resourceId: 'workflow_templates',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowBuilderWorkflow]: {
      isPublic: false,
      path: RoutePathType.WorkflowBuilderWorkflow,
      title: intl.formatMessage({
        id: 'Routes.workflowBuilderWorkflowTitle',
        defaultMessage: 'Workflow Builder',
        description: 'Title for the workflow builder workflow page route',
      }),
      resolvers: createWorkflowAdminResolvers(WorkflowBuilderPage),
      permissions: [
        {
          resourceId: 'workflow_templates',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowIntakeFormLink]: {
      isPublic: false,
      path: RoutePathType.WorkflowIntakeFormLink,
      title: intl.formatMessage({
        id: 'Routes.workflowIntakeFormTitle',
        defaultMessage: 'Workflow Intake Form',
        description: 'Title for the workflow intake form page route',
      }),
      resolvers: createWorkflowResolvers(WizardTicketForm),
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'edit',
        },
        {
          resourceId: 'workflow_templates',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.IntegrateWorkflowIntakeFormLink]: {
      isPublic: false,
      path: RoutePathType.IntegrateWorkflowIntakeFormLink,
      title: intl.formatMessage({
        id: 'Routes.workflowIntakeFormTitle',
        defaultMessage: 'Workflow Intake Form',
        description: 'Title for the workflow intake form page route',
      }),
      resolvers: createWorkflowResolvers(WizardTicketForm),
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'edit',
        },
        {
          resourceId: 'workflow_templates',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.IntegrateWorkflowAmendIntakeForm]: {
      isPublic: false,
      path: RoutePathType.IntegrateWorkflowAmendIntakeForm,
      title: intl.formatMessage({
        id: 'Routes.workflowAmendIntakeFormTitle',
        defaultMessage: 'Amend Intake Form',
        description: 'Title for the amend intake form page route',
      }),
      resolvers: createWorkflowResolvers(WizardTicketForm),
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'edit',
        },
        {
          resourceId: 'workflow_templates',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowTickets]: {
      isPublic: false,
      path: RoutePathType.WorkflowTickets,
      title: intl.formatMessage({
        id: 'Routes.workflowTicketsTitle',
        defaultMessage: 'Workflow Tickets',
        description: 'Title for the workflow tickets page route',
      }),
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          component: WorkflowTicketsPage,
        },
        {
          permission: canUseWorkflow,
          component: WorkflowTicketsPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowGroups]: {
      isPublic: false,
      path: RoutePathType.WorkflowGroups,
      flag: FlagType.GroupedTicketsAndDocs,
      title: intl.formatMessage({
        id: 'Routes.workflowGroupsTitle',
        defaultMessage: 'Groups',
        description: 'Title for the workflow groups page route',
      }),
      resolvers: [
        {
          component: WorkflowGroupsPage,
        },
      ],
      permissions: [
        {
          resourceId: 'entity_groups',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowGroupDetail]: {
      isPublic: false,
      path: RoutePathType.WorkflowGroupDetail,
      flag: FlagType.GroupedTicketsAndDocs,
      title: intl.formatMessage({
        id: 'Routes.workflowGroupDetailTitle',
        defaultMessage: 'Group Details',
        description: 'Title for the workflow group detail page route',
      }),
      resolvers: [
        {
          component: WorkflowGroupDetailPage,
        },
      ],
      permissions: [
        {
          resourceId: 'entity_groups',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowTicketsNew]: {
      isPublic: false,
      path: RoutePathType.WorkflowTicketsNew,
      title: intl.formatMessage({
        id: 'Routes.workflowTicketsNewTitle',
        defaultMessage: 'New Ticket',
        description: 'Title for the new ticket page route',
      }),
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          component: WorkflowTicketsNewPage,
        },
        {
          permission: canUseWorkflow,
          component: WorkflowTicketsNewPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowTicketsReroute]: {
      isPublic: false,
      path: RoutePathType.WorkflowTicketsReroute,
      title: intl.formatMessage({
        id: 'Routes.redirectingToTicketTitle',
        defaultMessage: 'Redirecting to Ticket',
        description: 'Title for the redirecting to ticket page route',
      }),
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          component: TicketRerouteRedirectPage,
        },
        {
          permission: canUseWorkflow,
          component: TicketRerouteRedirectPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
    [RoutePathType.WorkflowTicketsTicket]: {
      isPublic: false,
      path: RoutePathType.WorkflowTicketsTicket,
      title: intl.formatMessage({
        id: 'Routes.ticketDetailsTitle',
        defaultMessage: 'Ticket Details',
        description: 'Title for the ticket details page route',
      }),
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          component: TicketsViewPage,
        },
        {
          permission: canUseWorkflow,
          component: TicketsViewPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
    [RoutePathType.WorkflowCLMEvolutionTickets]: {
      isPublic: false,
      path: RoutePathType.WorkflowCLMEvolutionTickets,
      title: intl.formatMessage({
        id: 'Routes.workflowCLMEvolutionTicketsTitle',
        defaultMessage: 'Workflow Tickets',
        description: 'Title for the workflow tickets page route',
      }),
      flag: FlagType.CLMEvolutionPhase1,
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          component: WorkflowCLMEvolutionTicketsPage,
        },
        {
          permission: canUseWorkflow,
          component: WorkflowCLMEvolutionTicketsPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'view',
        },
      ],
      nestedRoutes: [
        {
          path: 'new',
          title: intl.formatMessage({
            id: 'Routes.workflowCLMEvolutionTicketsNewTitle',
            defaultMessage: 'New Ticket',
            description: 'Title for the new ticket page route',
          }),
          component: WorkflowCLMEvolutionTicketsNewPage,
        },
      ],
    },
    [RoutePathType.WorkflowCLMEvolutionTicketsDetail]: {
      isPublic: false,
      path: RoutePathType.WorkflowCLMEvolutionTicketsDetail,
      title: intl.formatMessage({
        id: 'Routes.workflowCLMEvolutionTicketDetailsTitle',
        defaultMessage: 'Ticket Details',
        description: 'Title for the ticket details page route',
      }),
      flag: FlagType.CLMEvolutionPhase1,
      resolvers: [
        {
          permission: checkIsWorkflowOnly,
          component: WorkflowCLMEvolutionTicketsTicketDetailPage,
        },
        {
          permission: canUseWorkflow,
          component: WorkflowCLMEvolutionTicketsTicketDetailPage,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'view',
        },
      ],
      nestedRoutes: [
        {
          path: 'tasks',
          title: intl.formatMessage({
            id: 'Routes.workflowCLMEvolutionTicketDetailsTitle',
            defaultMessage: 'Ticket Details',
            description: 'Title for the ticket details page route',
          }),
          component: TicketDetailsTasksTab,
        },
        {
          path: 'tasks/:taskId',
          title: intl.formatMessage({
            id: 'Routes.workflowCLMEvolutionTicketDetailsTitle',
            defaultMessage: 'Ticket Details',
            description: 'Title for the ticket details page route',
          }),
          component: TicketDetailsTasksTab,
        },
        {
          path: 'documents',
          title: intl.formatMessage({
            id: 'Routes.workflowCLMEvolutionTicketDetailsTitle',
            defaultMessage: 'Ticket Details',
            description: 'Title for the ticket details page route',
          }),
          component: TicketDetailsDocumentsTab,
        },
        {
          path: 'activity',
          title: intl.formatMessage({
            id: 'Routes.workflowCLMEvolutionTicketDetailsTitle',
            defaultMessage: 'Ticket Details',
            description: 'Title for the ticket details page route',
          }),
          component: TicketDetailsActivitiesTab,
        },
      ],
    },
    [RoutePathType.WorkflowCLMEvolutionWorkflows]: {
      isPublic: false,
      path: RoutePathType.WorkflowCLMEvolutionWorkflows,
      title: intl.formatMessage({
        id: 'Routes.workflowCLMEvolutionWorkflowsTitle',
        defaultMessage: 'Workflows',
        description: 'Title for the workflows page route',
      }),
      flag: FlagType.CLMEvolutionPhase1,
      resolvers: createWorkflowAdminResolvers(
        WorkflowCLMEvolutionWorkflowsPage,
      ),
      permissions: [
        {
          resourceId: 'workflow_templates',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowCLMEvolutionWorkflowsDetail]: {
      isPublic: false,
      path: RoutePathType.WorkflowCLMEvolutionWorkflowsDetail,
      title: intl.formatMessage({
        id: 'Routes.workflowCLMEvolutionDetailsTitle',
        defaultMessage: 'Workflow Details',
        description: 'Title for the workflow details page route',
      }),
      flag: FlagType.CLMEvolutionPhase1,
      resolvers: createWorkflowAdminResolvers(
        WorkflowCLMEvolutionWorkflowDetailPage,
      ),
      permissions: [
        {
          resourceId: 'workflow_templates',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WorkflowTicketsTicketReviewerVersionNumber]: {
      isPublic: false,
      path: RoutePathType.WorkflowTicketsTicketReviewerVersionNumber,
      title: intl.formatMessage({
        id: 'Routes.ticketVersionTitle',
        defaultMessage: 'Ticket Version',
        description: 'Title for the ticket version page route',
      }),
      resolvers: createWorkflowResolvers(TicketReviewerPage),
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.WordOnlineFile]: {
      isPublic: false,
      path: RoutePathType.WordOnlineFile,
      title: intl.formatMessage({
        id: 'Routes.wordForTheWebTitle',
        defaultMessage: 'Word for the web',
        description: 'Title for the Word for the web page route',
      }),
      resolvers: createWorkflowResolvers(WordOnlinePage),
      permissions: [
        {
          resourceId: 'workflow_tickets',
          resourceType: 'view',
        },
      ],
    },
    [RoutePathType.XRayLibrary]: {
      isPublic: false,
      path: RoutePathType.XRayLibrary,
      title: intl.formatMessage({
        id: 'Routes.xRayModelLibraryTitle',
        defaultMessage: 'Custom AI Model Library',
        description: 'Title for the Custom AI model library page route',
      }),
      flag: FlagType.DocumentXRayPrebuiltModels,
      resolvers: [
        {
          permission: canUseAutomation,
          component: XRayLibrary,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
    [RoutePathType.XRayLibraryAdmin]: {
      isPublic: false,
      path: RoutePathType.XRayLibraryAdmin,
      title: intl.formatMessage({
        id: 'Routes.xRayModelLibraryManagementTitle',
        defaultMessage: 'Custom AI Model Library Management',
        description:
          'Title for the Custom AI model library management page route',
      }),
      flag: FlagType.DocumentXRayPrebuiltModelsAdmin,
      resolvers: [
        {
          permission: canUseAutomation,
          component: XRayLibraryAdmin,
        },
        {
          redirectPath: RoutePathType.Home,
        },
      ],
    },
  };
};

export const publicRoutes = (intl: IntlShape) => ({
  [RoutePathType.Activation]: {
    isPublic: true,
    path: RoutePathType.Activation,
    title: intl.formatMessage({
      id: 'Routes.activationTitle',
      defaultMessage: 'Activate Evisort',
      description: 'Title for the activation page route',
    }),
    resolvers: createPassthroughResolvers(ActivationPage),
  },
  [RoutePathType.ChangePassword]: {
    isPublic: true,
    path: RoutePathType.ChangePassword,
    title: intl.formatMessage({
      id: 'Routes.changePasswordTitle',
      defaultMessage: 'Change password',
      description: 'Title for the change password page route',
    }),
    resolvers: createPassthroughResolvers(ChangePasswordPage),
  },
  [RoutePathType.ExternalDocumentViewer]: {
    isPublic: true,
    path: RoutePathType.ExternalDocumentViewer,
    title: intl.formatMessage({
      id: 'Routes.evisortDocumentViewerTitle',
      defaultMessage: 'Evisort Document viewer',
      description: 'Title for the Evisort document viewer page route',
    }),
    resolvers: createPassthroughResolvers(ExternalDocumentViewerPage),
  },
  [RoutePathType.ExpiredToken]: {
    isPublic: true,
    path: RoutePathType.ExpiredToken,
    title: intl.formatMessage({
      id: 'Routes.expiredTokenTitle',
      defaultMessage: 'Expired token',
      description: 'Title for the expired token page route',
    }),
    resolvers: createPassthroughResolvers(ExpiredTokenPage),
  },
  [RoutePathType.ForgotPassword]: {
    isPublic: true,
    path: RoutePathType.ForgotPassword,
    title: intl.formatMessage({
      id: 'Routes.resetPasswordTitle',
      defaultMessage: 'Reset password',
      description: 'Title for the reset password page route',
    }),
    resolvers: createPassthroughResolvers(ForgotPasswordPage),
  },
  [RoutePathType.Login]: {
    isPublic: true,
    path: RoutePathType.Login,
    title: intl.formatMessage({
      id: 'Routes.logInTitle',
      defaultMessage: 'Log In',
      description: 'Title for the log in page route',
    }),
    resolvers: createPassthroughResolvers(LoginPage),
  },
  [RoutePathType.LoginEmbed]: {
    isPublic: true,
    path: RoutePathType.LoginEmbed,
    title: intl.formatMessage({
      id: 'Routes.logInEmbedTitle',
      defaultMessage: 'Log In',
      description: 'Title for the log in embed page route',
    }),
    resolvers: createPassthroughResolvers(LoginEmbedPage),
  },
  [RoutePathType.LoginEmbedSuccess]: {
    isPublic: true,
    path: RoutePathType.LoginEmbedSuccess,
    title: intl.formatMessage({
      id: 'Routes.logInSuccessfulTitle',
      defaultMessage: 'Log in successful',
      description: 'Title for the log in successful page route',
    }),
    resolvers: createPassthroughResolvers(LoginEmbedSuccessPage),
  },
  [RoutePathType.Unauthorized]: {
    isPublic: true,
    path: RoutePathType.Unauthorized,
    title: intl.formatMessage({
      id: 'Routes.unauthorizedTitle',
      defaultMessage: 'Unauthorized',
      description: 'Title for the unauthorized page route',
    }),
    resolvers: createPassthroughResolvers(Unauthorized),
  },
  [RoutePathType.TicketEmailApproveConfirmation]: {
    isPublic: true,
    path: RoutePathType.TicketEmailApproveConfirmation,
    title: intl.formatMessage({
      id: 'Routes.ticketApproveViaEmailTitle',
      defaultMessage: 'Ticket Approve Via Email',
      description: 'Title for the ticket approve via email page route',
    }),
    resolvers: createPassthroughResolvers(TicketEmailApproveConfirmation),
  },
  [RoutePathType.TicketEmailRejectConfirmation]: {
    isPublic: true,
    path: RoutePathType.TicketEmailRejectConfirmation,
    title: intl.formatMessage({
      id: 'Routes.ticketRejectViaEmailTitle',
      defaultMessage: 'Ticket Rejct Via Email',
      description: 'Title for the ticket reject via email page route',
    }),
    resolvers: createPassthroughResolvers(TicketEmailRejectConfirmation),
  },
  [RoutePathType.TicketEmailActionError]: {
    isPublic: true,
    path: RoutePathType.TicketEmailActionError,
    title: intl.formatMessage({
      id: 'Routes.ticketActionLinkInvalidTitle',
      defaultMessage: 'Ticket Action Link Invalid',
      description: 'Title for the ticket action link invalid page route',
    }),
    resolvers: createPassthroughResolvers(TicketEmailActionError),
  },
  [RoutePathType.TicketEmailApproveComment]: {
    isPublic: true,
    path: RoutePathType.TicketEmailApproveComment,
    title: intl.formatMessage({
      id: 'Routes.ticketApproveWithCommentViaEmailTitle',
      defaultMessage: 'Ticket Approve With Comment Via Email',
      description:
        'Title for the ticket approve with comment via email page route',
    }),
    resolvers: createPassthroughResolvers(TicketEmailApproveComment),
  },
  [RoutePathType.TicketEmailRejectComment]: {
    isPublic: true,
    path: RoutePathType.TicketEmailRejectComment,
    title: intl.formatMessage({
      id: 'Routes.ticketRejectWithCommentViaEmailTitle',
      defaultMessage: 'Ticket Rejct With Comment Via Email',
      description:
        'Title for the ticket reject with comment via email page route',
    }),
    resolvers: createPassthroughResolvers(TicketEmailRejectComment),
  },
  [RoutePathType.WordOnlineError]: {
    isPublic: true,
    path: RoutePathType.WordOnlineError,
    title: intl.formatMessage({
      id: 'Routes.wordForTheWebErrorTitle',
      defaultMessage: 'Word for the web Error',
      description: 'Title for the Word for the web error page route',
    }),
    resolvers: createPassthroughResolvers(WordOnlineErrorPage),
  },
});

/** These route paths are reserved, and cannot be assigned as client workspaces or URL subroutes */
export const reservedRoutePaths = (intl: IntlShape) => [
  ...Object.values(publicRoutes(intl)).map((route) => route.path),
  RoutePathType.FoldersPermissionCheck,
];

export const getRoutes = (intl: IntlShape) =>
  ({
    ...publicRoutes(intl),
    ...privateRoutes(intl),
    [RoutePathType.NotFound]: {
      isPublic: true,
      path: RoutePathType.NotFound,
      title: intl.formatMessage({
        id: 'Routes.notFoundTitle',
        defaultMessage: 'Not found',
        description: 'Title for the not found page route',
      }),
      resolvers: createPassthroughResolvers(NoMatch),
    },
  }) as unknown as Routes;
